<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { GripVerticalIcon } from '@/components/ui/icons';
import { usePopoverTableStore } from '@/stores/popoverTableStore';

const props = defineProps<{
  field: string;
  dataType: string;
  data: any;
  classType: string;
}>();

const filterText = ref('');
type ColumnKey = 'name' | 'email' | 'categories';

const columnMinWidths: Record<ColumnKey, number> = {
  name: 60,
  email: 220,
  categories: 200,
};

const defaultColumnWidths: Record<ColumnKey, number> = {
  name: 60,
  email: 220,
  categories: 200,
};

const popoverTableStore = usePopoverTableStore();
const storageKey = computed(() => `contact-array-column-widths:${props.field}:${props.classType}`);

const columnWidths = ref<Record<ColumnKey, number>>({ ...defaultColumnWidths });

const isResizing = ref(false);
const activeColumn = ref<ColumnKey | null>(null);
const startX = ref(0);
const startWidth = ref(0);

// Get the children array data
// Field pattern: 'contacts.display_string' where 'contacts' is the array field
const childrenArray = computed(() => {
  const parts = props.field.split('.');
  if (parts.length !== 2 || parts[1] !== 'display_string') {
    return [];
  }
  const arrayField = parts[0];
  const arrayData = props.data[arrayField];
  return Array.isArray(arrayData) ? arrayData : [];
});

// Filter items based on filter text (searches contact name or email)
const filteredItems = computed(() => {
  if (!filterText.value.trim()) {
    return childrenArray.value;
  }
  const searchText = filterText.value.toLowerCase();
  return childrenArray.value.filter((item: any) => {
    const displayString = item?.display_string || '';
    const email = item?.email || '';
    const categories = formatCategories(item);
    return displayString.toLowerCase().includes(searchText) ||
           email.toLowerCase().includes(searchText) ||
           categories.toLowerCase().includes(searchText);
  });
});

const formatCategories = (item: any): string => {
  const categories = item?.categories;
  if (!categories) {
    return '';
  }
  if (Array.isArray(categories)) {
    return categories
      .map((category: any) => category?.display_string || category?.name || category?.short_name || '')
      .filter((categoryName: string) => categoryName !== '')
      .join(', ');
  }
  if (typeof categories === 'string') {
    return categories;
  }
  if (typeof categories === 'object') {
    return categories?.display_string || categories?.name || categories?.short_name || '';
  }
  return '';
};

const getColumnStyle = (columnKey: ColumnKey) => ({
  width: `${columnWidths.value[columnKey]}px`,
  minWidth: `${columnMinWidths[columnKey]}px`,
});

const normalizeColumnWidths = (raw: Partial<Record<ColumnKey, number>>): Record<ColumnKey, number> => ({
  name: Math.max(columnMinWidths.name, Number(raw.name ?? defaultColumnWidths.name)),
  email: Math.max(columnMinWidths.email, Number(raw.email ?? defaultColumnWidths.email)),
  categories: Math.max(columnMinWidths.categories, Number(raw.categories ?? defaultColumnWidths.categories)),
});

const loadStoredWidths = () => {
  const stored = popoverTableStore.getColumnWidths(storageKey.value);
  if (stored) {
    columnWidths.value = normalizeColumnWidths(stored);
  } else {
    columnWidths.value = { ...defaultColumnWidths };
  }
};

const handleResizeStart = (columnKey: ColumnKey, event: MouseEvent) => {
  isResizing.value = true;
  activeColumn.value = columnKey;
  startX.value = event.clientX;
  startWidth.value = columnWidths.value[columnKey];

  document.addEventListener('mousemove', handleResizeMove);
  document.addEventListener('mouseup', handleResizeEnd);
};

const handleResizeMove = (event: MouseEvent) => {
  if (!isResizing.value || !activeColumn.value) return;

  const deltaX = event.clientX - startX.value;
  const minWidth = columnMinWidths[activeColumn.value];
  const newWidth = Math.max(minWidth, startWidth.value + deltaX);

  columnWidths.value = {
    ...columnWidths.value,
    [activeColumn.value]: newWidth,
  };
  
  // Save to store immediately during resize
  popoverTableStore.setColumnWidth(storageKey.value, activeColumn.value, newWidth);
};

const handleResizeEnd = () => {
  isResizing.value = false;
  activeColumn.value = null;
  document.removeEventListener('mousemove', handleResizeMove);
  document.removeEventListener('mouseup', handleResizeEnd);
};

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', handleResizeMove);
  document.removeEventListener('mouseup', handleResizeEnd);
});

onMounted(() => {
  loadStoredWidths();
});

watch(
  storageKey,
  () => {
    loadStoredWidths();
  },
  { flush: 'post' },
);

watch(
  columnWidths,
  (value) => {
    popoverTableStore.setColumnWidths(storageKey.value, value);
  },
  { deep: true },
);

</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 overflow-hidden">
    <!-- Filter section with input -->
    <div class="py-3 border-b px-4">
      <Input
        v-model="filterText"
        placeholder="Filter contacts..."
      />
    </div>
    <!-- Scrollable table -->
    <div class="flex-1 min-h-0 overflow-auto px-4">
      <Table tableClass="min-w-max table-auto">
        <TableHeader>
          <TableRow>
            <TableHead :style="getColumnStyle('name')">
              <div class="flex items-center justify-between gap-2">
                <span>Name</span>
                <button
                  type="button"
                  class="shrink-0 h-5 w-5 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-col-resize"
                  aria-label="Resize Name column"
                  @mousedown.prevent.stop="(event) => handleResizeStart('name', event)"
                >
                  <GripVerticalIcon size="md" />
                </button>
              </div>
            </TableHead>
            <TableHead :style="getColumnStyle('email')">
              <div class="flex items-center justify-between gap-2">
                <span>Email</span>
                <button
                  type="button"
                  class="shrink-0 h-5 w-5 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-col-resize"
                  aria-label="Resize Email column"
                  @mousedown.prevent.stop="(event) => handleResizeStart('email', event)"
                >
                  <GripVerticalIcon size="md" />
                </button>
              </div>
            </TableHead>
            <TableHead :style="getColumnStyle('categories')">
              <div class="flex items-center justify-between gap-2">
                <span>Categories</span>
                <button
                  type="button"
                  class="shrink-0 h-5 w-5 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-col-resize"
                  aria-label="Resize Categories column"
                  @mousedown.prevent.stop="(event) => handleResizeStart('categories', event)"
                >
                  <GripVerticalIcon size="md" />
                </button>
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="filteredItems.length > 0">
            <TableRow
              v-for="(item, index) in filteredItems"
              :key="index"
            >
              <TableCell class="min-w-[60px]">
                {{ item?.display_string || '' }}
              </TableCell>
              <TableCell class="min-w-[220px]">
                {{ item?.email || '' }}
              </TableCell>
              <TableCell class="min-w-[200px] break-words">
                {{ formatCategories(item) }}
              </TableCell>
            </TableRow>
          </template>
          <TableRow v-else>
            <TableCell colspan="3" class="text-center text-muted-foreground py-6">
              No items found
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  </div>
</template>
